import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AuditTrailService } from '../../../services/audit-trail.service';
import { ProjectContextService } from '../../../services/project-context.service';
import { ProjectService } from '../../../services/project.service';
import { AuditEntryDto, AuditQueryFilter } from '../../../dtos/audit-entry';
import { ProjectState, STAGE_META, getStageStateClass } from '../../../dtos/project-state';
import { PaperInfoModalComponent } from './paper-info-modal/paper-info-modal.component';
import { BaseModalComponent } from '../../modals/base-modal/base-modal.component';

@Component({
  selector: 'app-audit-trail',
  standalone: true,
  imports: [CommonModule, FormsModule, PaperInfoModalComponent, BaseModalComponent],
  templateUrl: './audit-trail.component.html',
  styleUrls: ['./audit-trail.component.scss'],
})
export class AuditTrailComponent implements OnInit, OnDestroy {
  @Input() open = false;
  @Input() initialEntityId: number | null = null;
  @Output() openChange = new EventEmitter<boolean>();
  private auditService = inject(AuditTrailService);
  private ctx = inject(ProjectContextService);
  private projectService = inject(ProjectService);

  entries = signal<AuditEntryDto[]>([]);
  loading = signal(false);
  currentPage = signal(0);
  totalPages = signal(0);
  totalItems = signal(0);
  expandedPayloads = signal<Set<string>>(new Set());
  exportingCsv = signal(false);

  /** paperToProjectId of the paper to show in the info modal; null = modal closed */
  readonly selectedPaperId = signal<number | null>(null);
  readonly projectId = signal<string>('');

  /** Papers available for the paper filter dropdown */
  projectPapers = signal<{ paperToProjectId: number; title: string }[]>([]);
  private paperScopeCleared = false;

  filter: AuditQueryFilter = {};
  isPI = signal(false);

  readonly PROJECT_STATE_VALUES = Object.values(ProjectState);
  readonly STAGE_META = STAGE_META;

  /** Actor searchable-dropdown state */
  projectMembers = signal<any[]>([]);
  actorSearchText = '';
  showActorDropdown = false;
  selectedActorName = signal<string>('');

  /** Paper searchable-dropdown state */
  paperSearchText = '';
  showPaperDropdown = false;
  selectedPaperName = signal<string>('');
  paperSearchResults = signal<{ paperToProjectId: number; title: string }[]>([]);
  paperSearchLoading = signal(false);

  get filteredMembers(): any[] {
    const q = this.actorSearchText.toLowerCase().trim();
    if (!q) return this.projectMembers();
    return this.projectMembers().filter((m: any) => {
      const name = this.getMemberDisplayName(m).toLowerCase();
      return name.includes(q) || (m.email || '').toLowerCase().includes(q);
    });
  }

  ngOnInit(): void {
    this.isPI.set(this.ctx.isProjectPI());
    this.projectId.set(this.ctx.project()?.id ?? '');
    this.loadMembers();
    if (this.initialEntityId) {
      this.filter.entityId = String(this.initialEntityId);
      this.loadPaperTitle(this.initialEntityId);
    }
    this.loadEntries();
    this.loadProjectPapers();
  }

  ngOnDestroy(): void {
    if (this.paperSearchTimer) clearTimeout(this.paperSearchTimer);
  }

  close(): void {
    this.openChange.emit(false);
  }

  loadEntries(page: number = 0): void {
    this.loading.set(true);
    const projectId = this.ctx.project()?.id;
    if (!projectId) {
      this.loading.set(false);
      return;
    }

    this.auditService.getEntries(projectId, this.filter, page).subscribe({
      next: (response) => {
        if (page === 0) {
          this.entries.set(response.items);
        } else {
          this.entries.update((prev) => [...prev, ...response.items]);
        }
        this.currentPage.set(response.currentPage);
        this.totalPages.set(response.totalPages);
        this.totalItems.set(response.totalItems);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
      },
    });
  }

  applyFilters(): void {
    // Swap dates if from is after to
    if (this.filter.from && this.filter.to && this.filter.from > this.filter.to) {
      const tmp = this.filter.from;
      this.filter.from = this.filter.to;
      this.filter.to = tmp;
    }
    this.currentPage.set(0);
    this.loadEntries(0);
  }

  clearFilters(): void {
    this.filter = {};
    this.selectedActorName.set('');
    this.actorSearchText = '';
    this.showActorDropdown = false;
    this.selectedPaperName.set('');
    this.paperSearchText = '';
    this.paperSearchResults.set([]);
    this.showPaperDropdown = false;
    this.filter.entityId = undefined;
    if (this.initialEntityId) {
      this.paperScopeCleared = true;
    }
    this.applyFilters();
  }

  get scopedToPaper(): boolean {
    if (this.filter.entityId) return true;
    return !this.paperScopeCleared && !!this.initialEntityId;
  }

  loadMore(): void {
    if (this.loading()) return;
    if (this.currentPage() < this.totalPages() - 1) {
      this.loadEntries(this.currentPage() + 1);
    }
  }

  exportCsv(): void {
    const projectId = this.ctx.project()?.id;
    if (!projectId) return;
    this.exportingCsv.set(true);
    this.auditService.exportCsv(projectId, this.filter).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'audit-trail.csv';
        a.click();
        window.URL.revokeObjectURL(url);
        this.exportingCsv.set(false);
      },
      error: () => {
        this.exportingCsv.set(false);
      },
    });
  }

  togglePayload(entryId: string): void {
    this.expandedPayloads.update((set) => {
      const next = new Set(set);
      if (next.has(entryId)) {
        next.delete(entryId);
      } else {
        next.add(entryId);
      }
      return next;
    });
  }

  isPayloadExpanded(entryId: string): boolean {
    return this.expandedPayloads().has(entryId);
  }

  hasPayload(entry: AuditEntryDto): boolean {
    return !!entry.payload && Object.keys(entry.payload).length > 0;
  }

  formatStage(state: string): string {
    const meta = STAGE_META.find((m) => m.state === state);
    return meta?.label ?? state;
  }

  formatTimestamp(ts: string): string {
    if (!ts) return '—';
    const date = new Date(ts);
    return isNaN(date.getTime()) ? '—' : date.toLocaleString();
  }

  /** Paper title for the scope chip. Uses entityName from first matching entry. */
  scopePaperTitle(): string {
    const id = this.filter.entityId || (this.initialEntityId ? String(this.initialEntityId) : null);
    if (!id) return '';
    const numericId = Number(id);
    // Try loaded project papers first (faster, no extra request)
    const cached = this.projectPapers().find((p) => p.paperToProjectId === numericId);
    if (cached?.title) return cached.title;
    // Fallback to first entry with matching entityId
    const entry = this.entries().find((e) => e.entityId === numericId);
    if (entry?.entityName) return entry.entityName;
    return `Paper #${id}`;
  }

  getStageBadgeClass(state: string): string {
    return getStageStateClass(state);
  }

  getActionColor(action: string): string {
    if (action.includes('SCREENING')) return 'border-blue-500';
    if (action.includes('EXTRACTION')) return 'border-green-500';
    if (action.includes('IMPORT')) return 'border-purple-500';
    if (action.includes('STAGE') || action.includes('PROTOCOL') || action.includes('MEMBER')) {
      return 'border-gray-400';
    }
    if (action.includes('ANNOTATE') || action.includes('DELETE') || action.includes('RETRIEVAL')) {
      return 'border-amber-500';
    }
    return 'border-gray-300';
  }

  /** Open the paper-info modal for the paper referenced by this audit entry. */
  openPaperInfo(entry: AuditEntryDto): void {
    if (!entry.entityId || !this.projectId()) return;
    this.selectedPaperId.set(entry.entityId);
  }

  onPaperModalClosed(): void {
    this.selectedPaperId.set(null);
  }

  onPaperInfoViewAuditTrail(paperToProjectId: number): void {
    this.selectedPaperId.set(null);
    this.filter.entityId = String(paperToProjectId);
    const paper = this.projectPapers().find((p) => p.paperToProjectId === paperToProjectId);
    this.selectedPaperName.set(paper?.title || `Paper #${paperToProjectId}`);
    this.paperSearchText = this.selectedPaperName();
    this.applyFilters();
  }

  selectPaper(paper: { paperToProjectId: number; title: string }): void {
    this.filter.entityId = String(paper.paperToProjectId);
    this.selectedPaperName.set(paper.title || `Paper #${paper.paperToProjectId}`);
    this.paperSearchText = this.selectedPaperName();
    this.showPaperDropdown = false;
    this.applyFilters();
  }

  clearPaper(): void {
    this.filter.entityId = undefined;
    this.selectedPaperName.set('');
    this.paperSearchText = '';
    this.paperSearchResults.set([]);
    this.showPaperDropdown = false;
    this.applyFilters();
  }

  private paperSearchTimer: ReturnType<typeof setTimeout> | null = null;

  onPaperInputChange(value: string): void {
    this.paperSearchText = value;
    this.showPaperDropdown = true;
    if (this.paperSearchTimer) clearTimeout(this.paperSearchTimer);
    if (!value.trim()) {
      this.paperSearchResults.set([]);
      this.filter.entityId = undefined;
      this.selectedPaperName.set('');
      return;
    }
    this.paperSearchTimer = setTimeout(() => this.searchPapers(value.trim()), 250);
  }

  private searchPapers(query: string): void {
    const pid = this.projectId();
    if (!pid) return;
    this.paperSearchLoading.set(true);
    this.projectService.searchProjectPapers(pid, query).subscribe({
      next: (results) => {
        this.paperSearchResults.set(results);
        this.paperSearchLoading.set(false);
      },
      error: () => {
        this.paperSearchResults.set([]);
        this.paperSearchLoading.set(false);
      },
    });
  }

  onPaperFocus(): void {
    this.showPaperDropdown = true;
  }

  onPaperBlur(): void {
    setTimeout(() => {
      this.showPaperDropdown = false;
      if (this.filter.entityId && this.selectedPaperName()) {
        this.paperSearchText = this.selectedPaperName();
      }
    }, 200);
  }

  /** Load project papers for the paper filter dropdown. */
  private loadProjectPapers(): void {
    const pid = this.projectId();
    if (!pid) return;
    this.projectService.getProjectPapers(pid).subscribe({
      next: (papers) => this.projectPapers.set(papers),
      error: () => this.projectPapers.set([]),
    });
  }

  /** Fetch title by ptpId and populate search input when opened scoped to a paper. */
  private loadPaperTitle(ptpId: number): void {
    const pid = this.projectId();
    if (!pid) return;
    this.projectService.getPaperTitle(pid, ptpId).subscribe({
      next: (title) => {
        this.selectedPaperName.set(title);
        this.paperSearchText = title;
      },
      error: () => {
        this.selectedPaperName.set(`Paper #${ptpId}`);
        this.paperSearchText = `Paper #${ptpId}`;
      },
    });
  }

  /** Load all project members for the actor searchable dropdown. */
  private loadMembers(): void {
    const pid = this.projectId();
    if (!pid) return;
    this.projectService.getAllProjectResearchers(pid).subscribe({
      next: (members) => this.projectMembers.set(members),
      error: () => this.projectMembers.set([]),
    });
  }

  getMemberDisplayName(member: any): string {
    if (member.firstName || member.lastName)
      return `${member.firstName || ''} ${member.lastName || ''}`.trim();
    return member.email || 'Unknown';
  }

  selectActor(member: any): void {
    this.filter.actorId = String(member.id ?? '');
    this.selectedActorName.set(this.getMemberDisplayName(member));
    this.actorSearchText = this.getMemberDisplayName(member);
    this.showActorDropdown = false;
    this.applyFilters();
  }

  clearActor(): void {
    this.filter.actorId = undefined;
    this.selectedActorName.set('');
    this.actorSearchText = '';
    this.showActorDropdown = false;
    this.applyFilters();
  }

  onActorInputChange(value: string): void {
    this.actorSearchText = value;
    if (!value.trim()) {
      this.clearActor();
    }
    this.showActorDropdown = true;
  }

  onActorFocus(): void {
    this.showActorDropdown = true;
  }

  onActorBlur(): void {
    // Delay to allow click on dropdown item to register
    setTimeout(() => {
      this.showActorDropdown = false;
      // Restore selected name text if blurring away from a partial search
      if (this.filter.actorId && this.selectedActorName()) {
        this.actorSearchText = this.selectedActorName();
      }
    }, 200);
  }
}
